import React, { useState, useEffect, useCallback } from 'react';
import { employeeApi, payrollApi, projectApi } from '../services/api';
import { Button, Input, Select, Card, Modal, Badge } from '../components/UI';
import {
  Money, Plus, PencilSimple, Trash, Users, Receipt, CaretDown, CaretRight,
  CheckCircle, Clock, ArrowRight, CurrencyDollar
} from '@phosphor-icons/react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const fmt = (v) => new Intl.NumberFormat('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

export default function PayrollPage() {
  const [tab, setTab] = useState('employees');
  const [employees, setEmployees] = useState([]);
  const [runs, setRuns] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [showEmpModal, setShowEmpModal] = useState(false);
  const [showRunModal, setShowRunModal] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [error, setError] = useState('');
  const [empForm, setEmpForm] = useState({
    employee_code: '', name: '', designation: '', basic_salary: '', housing_allowance: '',
    transport_allowance: '', other_allowances: '', overtime_rate_per_hour: '',
    bank_name: '', iban: '', wps_mol_id: '', contract_type: 'full_time', join_date: '', project_id: ''
  });
  const [runForm, setRunForm] = useState({ period_month: new Date().getMonth() + 1, period_year: new Date().getFullYear(), currency: 'AED' });

  const load = useCallback(async () => {
    try {
      const [empRes, runRes, projRes] = await Promise.all([
        employeeApi.list({ active_only: false }), payrollApi.listRuns(), projectApi.list()
      ]);
      setEmployees(empRes.data);
      setRuns(runRes.data);
      setProjects(projRes.data);
    } catch (e) { setError('Failed to load data'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadPayslips = async (runId) => {
    setSelectedRun(runId);
    const res = await payrollApi.getPayslips(runId);
    setPayslips(res.data);
  };

  const handleEmpSubmit = async (e) => {
    e.preventDefault(); setError('');
    try {
      const data = { ...empForm, basic_salary: parseFloat(empForm.basic_salary) || 0,
        housing_allowance: parseFloat(empForm.housing_allowance) || 0,
        transport_allowance: parseFloat(empForm.transport_allowance) || 0,
        other_allowances: parseFloat(empForm.other_allowances) || 0,
        overtime_rate_per_hour: parseFloat(empForm.overtime_rate_per_hour) || 0 };
      if (editEmp) { await employeeApi.update(editEmp.id, data); }
      else { await employeeApi.create(data); }
      setShowEmpModal(false); setEditEmp(null); load();
      setEmpForm({ employee_code: '', name: '', designation: '', basic_salary: '', housing_allowance: '', transport_allowance: '', other_allowances: '', overtime_rate_per_hour: '', bank_name: '', iban: '', wps_mol_id: '', contract_type: 'full_time', join_date: '', project_id: '' });
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  const handleCreateRun = async (e) => {
    e.preventDefault(); setError('');
    try {
      await payrollApi.createRun(runForm);
      setShowRunModal(false); load();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to create payroll run'); }
  };

  const handleProcess = async (runId) => {
    if (!window.confirm('Process this payroll? Journal entries will be created.')) return;
    try { await payrollApi.processRun(runId); load(); if (selectedRun === runId) loadPayslips(runId); }
    catch (err) { setError(err.response?.data?.detail || 'Failed to process'); }
  };

  const handleDeleteRun = async (runId) => {
    if (!window.confirm('Delete this draft payroll run?')) return;
    try { await payrollApi.deleteRun(runId); setSelectedRun(null); setPayslips([]); load(); }
    catch (err) { setError(err.response?.data?.detail || 'Cannot delete'); }
  };

  const openEditEmp = (emp) => {
    setEditEmp(emp);
    setEmpForm({ employee_code: emp.employee_code, name: emp.name, designation: emp.designation || '',
      basic_salary: emp.basic_salary, housing_allowance: emp.housing_allowance,
      transport_allowance: emp.transport_allowance, other_allowances: emp.other_allowances,
      overtime_rate_per_hour: emp.overtime_rate_per_hour, bank_name: emp.bank_name || '',
      iban: emp.iban || '', wps_mol_id: emp.wps_mol_id || '', contract_type: emp.contract_type || 'full_time',
      join_date: emp.join_date || '', project_id: emp.project_id || '' });
    setShowEmpModal(true);
  };

  const exportPayslipPDF = (slip) => {
    const doc = new jsPDF();
    doc.setFontSize(18); doc.text('PAYSLIP', 105, 20, { align: 'center' });
    doc.setFontSize(10);
    const run = runs.find(r => r.id === slip.payroll_run_id);
    doc.text(`Period: ${run?.period_month}/${run?.period_year}`, 14, 35);
    doc.text(`Employee: ${slip.employee_name} (${slip.employee_code})`, 14, 42);
    const rows = [
      ['Basic Salary', fmt(slip.basic_salary)],
      ['Housing Allowance', fmt(slip.housing_allowance)],
      ['Transport Allowance', fmt(slip.transport_allowance)],
      ['Other Allowances', fmt(slip.other_allowances)],
      ['Overtime (' + (slip.overtime_hours || 0) + ' hrs)', fmt(slip.overtime_amount)],
      ['Gross Salary', fmt(slip.gross_salary)],
      ['Deductions', fmt(slip.total_deductions)],
      ['Net Salary', fmt(slip.net_salary)],
      ['EOS Accrual', fmt(slip.eos_accrual)],
    ];
    autoTable(doc, { startY: 50, head: [['Component', 'Amount (AED)']], body: rows });
    doc.save(`payslip_${slip.employee_code}_${run?.period_month}_${run?.period_year}.pdf`);
  };

  const getProjectName = (pid) => projects.find(p => p.id === pid)?.name || '-';
  const tabs = [{ key: 'employees', label: 'Employees', icon: Users }, { key: 'payroll', label: 'Payroll Runs', icon: Receipt }];

  return (
    <div className="space-y-4" data-testid="payroll-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]" data-testid="payroll-heading">Payroll Management</h1>
          <p className="text-sm text-[#666]">Employees, salary processing, payslips & WPS</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-[#E0E0E0]" data-testid="payroll-tabs">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key ? 'border-[#1A1F36] text-[#1A1F36]' : 'border-transparent text-[#888] hover:text-[#555]'}`}
            data-testid={`tab-${t.key}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {error && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm">{error}</div>}

      {tab === 'employees' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-[#666]">{employees.length} employee(s)</span>
            <Button onClick={() => { setEditEmp(null); setEmpForm({ employee_code: '', name: '', designation: '', basic_salary: '', housing_allowance: '', transport_allowance: '', other_allowances: '', overtime_rate_per_hour: '', bank_name: '', iban: '', wps_mol_id: '', contract_type: 'full_time', join_date: '', project_id: '' }); setShowEmpModal(true); }} size="sm" data-testid="add-employee-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Employee
            </Button>
          </div>
          {employees.length === 0 ? (
            <Card className="p-8 text-center text-[#666]">No employees added yet</Card>
          ) : (
            <Card className="divide-y divide-[#F0F0F0]">
              {employees.map(emp => (
                <div key={emp.id} className="flex items-center justify-between p-3 hover:bg-[#FAFAFA]" data-testid={`emp-row-${emp.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-[#002FA7]/10 rounded-lg flex items-center justify-center">
                      <Users className="w-4 h-4 text-[#002FA7]" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-[#0A0A0A]">{emp.name} <span className="text-[#999] text-xs font-mono">({emp.employee_code})</span></p>
                      <div className="flex gap-3 text-xs text-[#666]">
                        <span>{emp.designation}</span>
                        <span>Basic: {fmt(emp.basic_salary)}</span>
                        <span>Project: {getProjectName(emp.project_id)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-bold text-[#002FA7] mr-2">{fmt(emp.basic_salary + emp.housing_allowance + emp.transport_allowance + emp.other_allowances)}</span>
                    <button onClick={() => openEditEmp(emp)} className="p-1 text-[#666] hover:text-[#002FA7]" data-testid={`edit-emp-${emp.id}`}><PencilSimple className="w-4 h-4" /></button>
                    <button onClick={async () => { if (window.confirm('Delete?')) { await employeeApi.delete(emp.id); load(); } }} className="p-1 text-[#666] hover:text-[#FF3333]" data-testid={`del-emp-${emp.id}`}><Trash className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </Card>
          )}
        </div>
      )}

      {tab === 'payroll' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-[#666]">{runs.length} payroll run(s)</span>
            <Button onClick={() => setShowRunModal(true)} size="sm" data-testid="create-run-btn">
              <Plus className="w-4 h-4 mr-1" /> New Payroll Run
            </Button>
          </div>
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-1 space-y-2">
              {runs.length === 0 ? <Card className="p-6 text-center text-[#666]">No payroll runs yet</Card> : runs.map(run => (
                <Card key={run.id} className={`p-3 cursor-pointer transition-colors ${selectedRun === run.id ? 'ring-2 ring-[#002FA7]' : ''}`}
                  onClick={() => loadPayslips(run.id)} data-testid={`run-card-${run.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-sm text-[#0A0A0A]">{run.period_month}/{run.period_year}</p>
                      <div className="flex gap-2 text-xs text-[#666] mt-0.5">
                        <span>{run.employee_count} employees</span>
                        <Badge variant={run.status === 'processed' ? 'success' : 'default'}>{run.status}</Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-[#002FA7]">{fmt(run.total_net)}</p>
                      <p className="text-[10px] text-[#999]">Net</p>
                    </div>
                  </div>
                  {run.status === 'draft' && (
                    <div className="flex gap-2 mt-2 pt-2 border-t border-[#F0F0F0]">
                      <Button size="sm" onClick={(e) => { e.stopPropagation(); handleProcess(run.id); }} data-testid={`process-run-${run.id}`}>
                        <CheckCircle className="w-3 h-3 mr-1" /> Process
                      </Button>
                      <Button size="sm" variant="danger" onClick={(e) => { e.stopPropagation(); handleDeleteRun(run.id); }} data-testid={`del-run-${run.id}`}>
                        <Trash className="w-3 h-3 mr-1" /> Delete
                      </Button>
                    </div>
                  )}
                </Card>
              ))}
            </div>
            <div className="lg:col-span-2">
              {!selectedRun ? (
                <Card className="p-8 text-center text-[#666]"><Receipt className="w-10 h-10 mx-auto mb-3 text-[#E0E0E0]" />Select a payroll run to view payslips</Card>
              ) : (
                <Card className="divide-y divide-[#F0F0F0]" data-testid="payslips-list">
                  <div className="p-3 flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#0A0A0A]">{payslips.length} Payslip(s)</span>
                  </div>
                  {payslips.map(slip => (
                    <div key={slip.id} className="p-3 hover:bg-[#FAFAFA]" data-testid={`payslip-${slip.id}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm text-[#0A0A0A]">{slip.employee_name} <span className="text-xs text-[#999]">({slip.employee_code})</span></p>
                          <div className="flex flex-wrap gap-3 text-xs text-[#666] mt-0.5">
                            <span>Basic: {fmt(slip.basic_salary)}</span>
                            <span>OT: {slip.overtime_hours}h = {fmt(slip.overtime_amount)}</span>
                            <span>Gross: {fmt(slip.gross_salary)}</span>
                            <span>Ded: {fmt(slip.total_deductions)}</span>
                            <span className="text-[#00C853]">EOS: {fmt(slip.eos_accrual)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            <p className="text-sm font-bold text-[#00C853]">{fmt(slip.net_salary)}</p>
                            <p className="text-[10px] text-[#999]">Net</p>
                          </div>
                          <button onClick={() => exportPayslipPDF(slip)} className="p-1.5 text-[#666] hover:text-[#002FA7] hover:bg-[#002FA7]/5 rounded" title="Download PDF" data-testid={`pdf-${slip.id}`}>
                            <Receipt className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </Card>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Employee Modal */}
      <Modal isOpen={showEmpModal} onClose={() => { setShowEmpModal(false); setEditEmp(null); }} title={editEmp ? 'Edit Employee' : 'Add Employee'}>
        <form onSubmit={handleEmpSubmit} className="space-y-3" data-testid="emp-form">
          {error && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <Input label="Employee Code" value={empForm.employee_code} onChange={e => setEmpForm({...empForm, employee_code: e.target.value})} required data-testid="emp-code-input" />
            <Input label="Full Name" value={empForm.name} onChange={e => setEmpForm({...empForm, name: e.target.value})} required data-testid="emp-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Designation" value={empForm.designation} onChange={e => setEmpForm({...empForm, designation: e.target.value})} />
            <Select label="Contract Type" value={empForm.contract_type} onChange={e => setEmpForm({...empForm, contract_type: e.target.value})}>
              <option value="full_time">Full Time</option><option value="part_time">Part Time</option><option value="contract">Contract</option>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Basic Salary (AED)" type="number" value={empForm.basic_salary} onChange={e => setEmpForm({...empForm, basic_salary: e.target.value})} required data-testid="emp-salary-input" />
            <Input label="Housing Allowance" type="number" value={empForm.housing_allowance} onChange={e => setEmpForm({...empForm, housing_allowance: e.target.value})} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Transport Allowance" type="number" value={empForm.transport_allowance} onChange={e => setEmpForm({...empForm, transport_allowance: e.target.value})} />
            <Input label="Other Allowances" type="number" value={empForm.other_allowances} onChange={e => setEmpForm({...empForm, other_allowances: e.target.value})} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="OT Rate/Hour" type="number" value={empForm.overtime_rate_per_hour} onChange={e => setEmpForm({...empForm, overtime_rate_per_hour: e.target.value})} />
            <Input label="Join Date" type="date" value={empForm.join_date} onChange={e => setEmpForm({...empForm, join_date: e.target.value})} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Bank Name" value={empForm.bank_name} onChange={e => setEmpForm({...empForm, bank_name: e.target.value})} />
            <Input label="IBAN" value={empForm.iban} onChange={e => setEmpForm({...empForm, iban: e.target.value})} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="WPS/MOL ID" value={empForm.wps_mol_id} onChange={e => setEmpForm({...empForm, wps_mol_id: e.target.value})} />
            <Select label="Project" value={empForm.project_id} onChange={e => setEmpForm({...empForm, project_id: e.target.value})}>
              <option value="">-- None --</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => { setShowEmpModal(false); setEditEmp(null); }}>Cancel</Button>
            <Button type="submit" data-testid="emp-submit-btn">{editEmp ? 'Update' : 'Add Employee'}</Button>
          </div>
        </form>
      </Modal>

      {/* Payroll Run Modal */}
      <Modal isOpen={showRunModal} onClose={() => setShowRunModal(false)} title="Create Payroll Run">
        <form onSubmit={handleCreateRun} className="space-y-3" data-testid="run-form">
          {error && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <Select label="Month" value={runForm.period_month} onChange={e => setRunForm({...runForm, period_month: parseInt(e.target.value)})}>
              {[...Array(12)].map((_, i) => <option key={i+1} value={i+1}>{new Date(2000, i).toLocaleString('default', { month: 'long' })}</option>)}
            </Select>
            <Input label="Year" type="number" value={runForm.period_year} onChange={e => setRunForm({...runForm, period_year: parseInt(e.target.value)})} />
          </div>
          <Select label="Currency" value={runForm.currency} onChange={e => setRunForm({...runForm, currency: e.target.value})}>
            <option value="AED">AED</option><option value="USD">USD</option><option value="EUR">EUR</option><option value="GBP">GBP</option><option value="SAR">SAR</option>
          </Select>
          <p className="text-xs text-[#888]">Payslips will be auto-generated for all active employees. Overtime will be derived from manpower logs.</p>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setShowRunModal(false)}>Cancel</Button>
            <Button type="submit" data-testid="run-submit-btn">Generate Payroll</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
